import { Component, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { AuthStore } from '../../../store/auth';
import { SidebarItem } from '../../../store/models/sidebar-item.model';
import { SidebarSection } from '../../../store/models/sidebar-section.model';
import { UiStore } from '../../../store/ui';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { OneSignalLoaderService } from '../../../services/onesignal-loader.service';
import { FeedbackService } from '../../../services/feedback.service';
import { AuthService } from '../../../services/auth.service';
import { PermissionsService } from '../../../services/permissions.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'lib-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, FormsModule],
  templateUrl: './sidebar.component.html',
})
export class SidebarComponent implements OnInit, OnDestroy {
  companyLogoUrl = input<string>('public/shared/images/logos/logotipo.svg');
  public isOpen$: Observable<boolean>;
  public sidebarSections$: Observable<SidebarSection[]>;
  public sidebarSections: SidebarSection[] = [];
  private _feedback = inject(FeedbackService);

  // OneSignal notification controls
  public notificationsEnabled = signal<boolean>(false);
  public notificationsInitialized = signal<boolean>(false);

  private _unsubscribe$ = new Subject<void>();

  constructor(
    private _store: Store,
    private _router: Router,
    private _oneSignalService: OneSignalLoaderService,
    private _feedbackService: FeedbackService,
    private _authService: AuthService,
    private _permissionsService: PermissionsService
  ) {
    this.isOpen$ = this._store.select(UiStore.selectors.selectIsSidebarOpen);
    this.sidebarSections$ = this._store.select(UiStore.selectors.selectSidebarItems);
  }

  ngOnInit(): void {
    this.sidebarSections$.pipe(takeUntil(this._unsubscribe$)).subscribe((sections) => {
      const sectionsCopy = JSON.parse(JSON.stringify(sections));

      this.sidebarSections = sectionsCopy
        .map((section: SidebarSection) => ({
          ...section,
          items: this._initializeMenuItems(this._filterMenuItemsByPermissions(section.items)),
        }))
        .filter((section: { items: SidebarItem[] }) => section.items.length > 0);
    });

    this._checkNotificationStatus();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  toggleSubMenu(item: SidebarItem): void {
    item.isExpanded = !item.isExpanded;
  }

  onCloseSidebar(): void {
    this._store.dispatch(UiStore.actions.toggleSidebar());
  }

  /**
   * Verifica y establece el estado inicial de las notificaciones
   */
  private async _checkNotificationStatus(): Promise<void> {
    try {
      // Verifica el estado actual de suscripción
      this.notificationsEnabled.set(this._oneSignalService.isSubscribed);
      this.notificationsInitialized.set(true);
    } catch (error) {
      console.error('Error checking notification status', error);
      this.notificationsEnabled.set(false);
      this.notificationsInitialized.set(false);
    }
  }

  /**
   * Maneja el cambio de estado del switch de notificaciones
   */
  async toggleNotifications(enabled: boolean): Promise<void> {
    try {
      if (enabled) {
        // PASO 1: Si intenta activar, solicitar permiso si no lo tiene
        if (!this._oneSignalService.hasPermission) {
          const permissionGranted = await this._oneSignalService.requestPushPermission();

          // Si el permiso fue negado, apagamos el switch INMEDIATAMENTE
          if (!permissionGranted) {
            const permissionStatus = this._oneSignalService.getNotificationPermission();

            // Si el permiso es 'denied', mostrar mensaje de error
            if (permissionStatus === 'denied') {
              this._feedbackService.showError({
                message:
                  'El navegador ha bloqueado las notificaciones. Para habilitarlas, cambie la configuración en su navegador.',
                skipTranslation: true,
              });
            }

            this.notificationsEnabled.set(false);
            return;
          }
        }

        // PASO 2: Intenta activar las notificaciones
        await this._oneSignalService.enableNotifications();
        this.notificationsEnabled.set(true);

        // PASO 3: Actualizar el token en el servidor
        const token = this._oneSignalService.pushSubscriptionId;
        if (token) {
          this._authService.enableNotifications(token).subscribe({
            next: (response) => console.log('Token actualizado correctamente', response),
            error: (error) => console.error('Error al actualizar token', error),
          });
        }
      } else {
        // Si intenta desactivar, simplemente desactivamos
        await this._oneSignalService.disableNotifications();
        this.notificationsEnabled.set(false);
      }
    } catch (error) {
      console.error('Error al gestionar notificaciones:', error);
      // Si hay un error, asegurar que el switch refleje el estado real
      this.notificationsEnabled.set(this._oneSignalService.isSubscribed);
    }
  }

  private _initializeMenuItems(items: SidebarItem[]): SidebarItem[] {
    return items.map((item) => {
      const shouldExpand = this._shouldExpandMenuItem(item);
      return {
        ...item,
        isExpanded: shouldExpand || item.isExpanded || false,
        children: item.children ? this._initializeMenuItems(item.children) : undefined,
      };
    });
  }

  private _filterMenuItemsByPermissions(items: SidebarItem[]): SidebarItem[] {
    return items.reduce((acc, item) => {
      const filteredChildren = item.children
        ? this._filterMenuItemsByPermissions(item.children)
        : undefined;

      const hasPermission = this._permissionsService.hasPermissions(item.permissions);

      if (hasPermission && (!filteredChildren || filteredChildren.length > 0)) {
        acc.push({
          ...item,
          children: filteredChildren,
        });
      }

      return acc;
    }, [] as SidebarItem[]);
  }

  private _shouldExpandMenuItem(item: SidebarItem): boolean {
    if (!item.children || item.children.length === 0) {
      return false;
    }

    const currentUrl = this._router.url;

    // Check if any child route is active
    return item.children.some((child) => {
      if (child.route && currentUrl.startsWith(child.route)) {
        return true;
      }
      return false;
    });
  }

  onLogout(): void {
    this._feedback.showConfirm({
      title: '¿Deseas cerrar sesión?',
      message: '',
      skipTranslation: true,
      cancelLabel: 'Regresar',
      presentation: 'modal',
      onAction: () => this._store.dispatch(AuthStore.actions.logout()),
      actionLabel: 'Cerrar sesión',
    });
  }
}
